import { useHandleSignInCallback, useLogto } from '@logto/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { clearLogtoStorage } from '../auth/recovery';
import { useTheme } from '../contexts/ThemeContext';

const RETURN_TO_KEY = 'helloant.returnTo';
const LOGIN_TIMEOUT_MS = import.meta.env.LOGIN_TIMEOUT_MS || 6000;

export function LoginCallback() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { signOut } = useLogto();
  const [stuck, setStuck] = useState(false);

  const hasCode =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('code');

  useHandleSignInCallback(() => {
    let target = '/';
    try {
      const stashed = sessionStorage.getItem(RETURN_TO_KEY);
      if (stashed) {
        sessionStorage.removeItem(RETURN_TO_KEY);
        // Convert absolute URL back to a path so react-router handles it.
        const url = new URL(stashed);
        if (url.origin === window.location.origin) {
          target = `${url.pathname}${url.search}${url.hash}`;
        }
      }
    } catch {
      // Fall through to '/'.
    }
    navigate(target, { replace: true });
  });

  useEffect(() => {
    if (!hasCode) return;
    const timer = window.setTimeout(() => setStuck(true), LOGIN_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [hasCode]);

  const handleReset = async () => {
    clearLogtoStorage();
    try {
      // Best-effort server-side revoke. If it throws (network error,
      // expired session, etc.), fall back to the local-only clear above.
      await signOut(`${window.location.origin}/logout`);
    } catch {
      window.location.replace('/login');
    }
  };

  const failed = !hasCode;

  return (
    <div className={`auth-screen theme-${theme}`}>
      <div className="loading-screen">
        <div>{failed ? t('signInError') : t('signingIn')}</div>
        {(failed || stuck) && (
          <button type="button" className="link-btn" onClick={handleReset}>
            {t('signInRetry')}
          </button>
        )}
      </div>
    </div>
  );
}
